import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Actions,
  BackIcon,
  BrandHeaderRow,
  BrandLogo,
  Button,
  Card,
  Header,
  HeaderIconButton,
  HeaderText,
  Message,
  Muted,
  Page,
  Shell,
  Subtitle,
  Title
} from '../components/PageTheme';
import useBackNavigation from '../utils/useBackNavigation';

const HeaderActions = styled(Actions)`
  align-self: flex-start;
  margin-left: auto;
`;

const PledgeList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const PledgeItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.1);
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.045);

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PledgeName = styled.div`
  color: #f8fafc;
  font-size: 1.15rem;
  font-weight: 950;
`;

const InfoCard = styled(Card)`
  margin-bottom: 1rem;
  color: rgba(226, 232, 240, 0.78);
  font-weight: 800;
  line-height: 1.35;
`;

const formatDateTime = (time) => {
  return new Date(time).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const isPledgeEditable = (pledge, currentTime) => {
  const createdAt = new Date(pledge.createdAt).getTime();

  return pledge.active && !Number.isNaN(createdAt) && currentTime - createdAt <= 3 * 60 * 1000;
};

const isPendingPledge = (pledge, currentTime) => {
  const dueAt = new Date(pledge.time).getTime();

  return pledge.active && !Number.isNaN(dueAt) && dueAt > currentTime;
};

const ManagePledges = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/settings');
  const [pledges, setPledges] = useState([]);
  const [locations, setLocations] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState({});
  const [confirmingDeleteId, setConfirmingDeleteId] = useState('');
  const [error, setError] = useState('');

  const locationNameById = useMemo(() => {
    return locations.reduce((result, location) => ({
      ...result,
      [location.id]: location.name
    }), {});
  }, [locations]);

  const activePledges = useMemo(() => {
    return pledges.filter((pledge) => isPendingPledge(pledge, currentTime));
  }, [pledges, currentTime]);

  const fetchData = useCallback(async () => {
    try {
      const idToken = await user.getIdToken();
      const [pledgesResponse, locationsResponse] = await Promise.all([
        axios.get(`${window.RUNTIME_CONFIG.apiUrl}/alarms`, {
          params: { active: true },
          headers: { Authorization: `Bearer ${idToken}` }
        }),
        axios.get(`${window.RUNTIME_CONFIG.apiUrl}/locations`, {
          headers: { Authorization: `Bearer ${idToken}` }
        })
      ]);

      setPledges(pledgesResponse.data.items || pledgesResponse.data);
      setLocations(locationsResponse.data);
    } catch (error) {
      console.error('Error fetching pledges:', error);
      setError('Failed to fetch pledges');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const handleDelete = async (pledgeId) => {
    if (confirmingDeleteId !== pledgeId) {
      setConfirmingDeleteId(pledgeId);
      return;
    }

    setDeleting((current) => ({ ...current, [pledgeId]: true }));
    setError('');

    try {
      await axios.delete(`${window.RUNTIME_CONFIG.apiUrl}/alarms/${pledgeId}`, {
        headers: { Authorization: `Bearer ${await user.getIdToken()}` }
      });

      setPledges((currentPledges) => currentPledges.filter((pledge) => pledge.id !== pledgeId));
      setConfirmingDeleteId('');
    } catch (error) {
      console.error('Error deleting pledge:', error);
      setError(error.response?.data?.message || 'Failed to delete pledge');
    } finally {
      setDeleting((current) => ({ ...current, [pledgeId]: false }));
    }
  };

  return (
    <Page>
      <Shell>
        <Header>
          <HeaderText>
            <BrandHeaderRow>
              <BrandLogo />
              <HeaderActions>
                <HeaderIconButton type="button" aria-label="Back" onClick={goBack}>
                  <BackIcon aria-hidden="true" />
                </HeaderIconButton>
              </HeaderActions>
            </BrandHeaderRow>
            <Title>Manage pledges.</Title>
            <Subtitle>Edit or delete pledges while they are still in the short edit window.</Subtitle>
          </HeaderText>
        </Header>

        <InfoCard>
          Pledges can only be edited or deleted for 3 minutes after they are created. After that, the pledge is locked.
        </InfoCard>

        {error && <Message $error>{error}</Message>}

        <PledgeList>
          {loading ? (
            <Card>Loading pledges...</Card>
          ) : activePledges.length === 0 ? (
            <Card>No active pledges.</Card>
          ) : (
            activePledges.map((pledge) => {
              const editable = isPledgeEditable(pledge, currentTime);

              return (
                <PledgeItem key={pledge.id}>
                  <div>
                    <PledgeName>{locationNameById[pledge.locationId] || 'Saved place'}</PledgeName>
                    <Muted>Due {formatDateTime(pledge.time)}</Muted>
                    {!editable && <Muted>Edit window closed</Muted>}
                  </div>
                  <Actions>
                    <Button
                      type="button"
                      disabled={!editable}
                      onClick={() => navigate(`/alarms/${pledge.id}/edit`)}
                    >
                      Edit
                    </Button>
                    {confirmingDeleteId === pledge.id && (
                      <Button $secondary type="button" onClick={() => setConfirmingDeleteId('')}>
                        Cancel
                      </Button>
                    )}
                    <Button
                      $danger
                      type="button"
                      disabled={!editable || deleting[pledge.id]}
                      onClick={() => handleDelete(pledge.id)}
                    >
                      {deleting[pledge.id] ? 'Deleting...' : confirmingDeleteId === pledge.id ? 'Confirm delete' : 'Delete'}
                    </Button>
                  </Actions>
                </PledgeItem>
              );
            })
          )}
        </PledgeList>
      </Shell>
    </Page>
  );
};

export default ManagePledges;
